"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

interface ArticleContentProps {
	html: string;
	className?: string;
}

export function ArticleContent({ html, className }: ArticleContentProps) {
	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.5, delay: 0.2 }}
			className={cn(
				"prose prose-base md:prose-lg prose-neutral dark:prose-invert max-w-none break-words",

				"prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground",
				"prose-h1:text-3xl md:prose-h1:text-4xl prose-h1:mt-8 prose-h1:mb-4",
				"prose-h2:text-2xl md:prose-h2:text-3xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:border-b prose-h2:border-border prose-h2:pb-2",
				"prose-h3:text-xl md:prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3",
				"prose-h4:text-lg md:prose-h4:text-xl prose-h4:mt-6 prose-h4:mb-2",

				"prose-p:leading-relaxed prose-p:text-muted-foreground",
				"prose-strong:text-foreground prose-strong:font-semibold",
				"prose-em:italic",

				"prose-a:text-primary prose-a:font-medium prose-a:no-underline",
				"hover:prose-a:underline prose-a:transition-colors",

				"prose-ul:my-4 prose-ul:list-disc prose-ul:pl-6",
				"prose-ol:my-4 prose-ol:list-decimal prose-ol:pl-6",
				"prose-li:text-muted-foreground prose-li:my-1",

				"prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8",
				"prose-img:w-full prose-img:h-auto prose-img:object-cover",

				"prose-blockquote:border-l-4 prose-blockquote:border-primary",
				"prose-blockquote:bg-muted/50 prose-blockquote:rounded-r-lg",
				"prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-6",
				"prose-blockquote:italic prose-blockquote:text-muted-foreground",
				"prose-blockquote:not-italic prose-blockquote:font-normal",

				"prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded",
				"prose-code:text-sm prose-code:font-mono prose-code:before:content-none prose-code:after:content-none",

				"prose-pre:bg-zinc-900 dark:prose-pre:bg-zinc-950",
				"prose-pre:rounded-xl prose-pre:p-4 prose-pre:overflow-x-auto",
				"prose-pre:text-sm",

				"prose-table:border-collapse prose-table:w-full prose-table:my-6",
				"prose-thead:border-b prose-thead:border-border",
				"prose-th:text-left prose-th:font-semibold prose-th:py-3 prose-th:px-4",
				"prose-td:py-3 prose-td:px-4 prose-td:border-b prose-td:border-border/50",

				"prose-figure:my-8",
				"prose-figcaption:text-center prose-figcaption:text-sm prose-figcaption:text-muted-foreground prose-figcaption:mt-2",

				"prose-hr:border-border prose-hr:my-8",

				"[&_iframe]:rounded-xl [&_iframe]:w-full [&_iframe]:aspect-video [&_iframe]:my-6",
				"[&_video]:rounded-xl [&_video]:w-full [&_video]:my-6",

				"[&_.shopify-section]:my-8",

				className,
			)}
			// biome-ignore lint/security/noDangerouslySetInnerHtml: Content is trusted
			dangerouslySetInnerHTML={{ __html: html }}
		/>
	);
}
